<template>
    <el-dialog :title="roomId ? '编辑房间' : '添加房间'" :model-value="modelValue" @closed="closed" width="640px">
        <el-form
            :model="formData"
            ref="roomeForm"
            label-width="120px"
            label-position="right"
            :rules="rules"
            size="default"
            style="max-width: 540px"
        >
            <el-form-item label="区域" prop="building">
                <el-cascader
                    v-model="formData.building"
                    :options="unit"
                    @change="changeBuilding"
                    :props="{ label: 'name', value: 'id' }"
                    style="width: 100%"
                ></el-cascader>
            </el-form-item>
            <el-form-item label="房间名称" prop="name">
                <el-input v-model="formData.name" placeholder="请输入房间名称" />
            </el-form-item>
            <el-form-item label="房间类型" prop="room_type">
                <el-select v-model="formData.room_type" placeholder="请选择" style="width: 100%">
                    <el-option v-for="item in roomType" :key="item.id" :label="item.name" :value="item.id"></el-option>
                </el-select>
            </el-form-item>
            <el-form-item label="房间面积" prop="area_size">
                <el-input v-model="formData.area_size" placeholder="请输入房间面积">
                    <template #append>㎡</template>
                </el-input>
            </el-form-item>
            <!--            <el-form-item label="房间容量" prop="capacity">
                <el-input-number v-model="formData.capacity" />
            </el-form-item>-->
            <el-form-item label="显示排序" prop="list_order">
                <el-input-number v-model="formData.list_order" class="mx-4" :min="0" :max="999999" controls-position="right" />
            </el-form-item>
            <el-form-item label="状态" prop="status">
                <el-radio-group v-model="formData.status">
                    <el-radio v-for="item in statusOptions" :key="item.label" :value="item.value">{{ item.label }}</el-radio>
                </el-radio-group>
            </el-form-item>
            <el-form-item label="备注" prop="remark">
                <el-input v-model="formData.remark" type="textarea" placeholder="请输入备注" />
            </el-form-item>
        </el-form>
        <template #footer>
            <span class="dialog-footer">
                <el-button @click="closed">取消</el-button>
                <el-button type="primary" :loading="submitLoading" @click="onConfirm">确定</el-button>
            </span>
        </template>
    </el-dialog>
</template>

<script setup>
import { defineEmits, defineProps, nextTick, ref, watch } from 'vue'
import { addRooming, getRoomDetail, putRooming } from '@/api/building'
import { ElMessage } from 'element-plus'
import { findPatentValue } from '@/utils/common.js'

const roomeForm = ref(null)
const emits = defineEmits(['update:modelValue', 'updateTable'])
const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true
    },
    // 运营单位、区域数据源
    sourceData: {
        type: Array,
        required: true
    },
    // tree选择节点的id 数组  用于运营单位、区域数据回显
    cascaderData: {
        type: Array,
        required: true
    },
    roomId: {
        type: String,
        required: true
    },
    statusOptions: {
        type: Array,
        required: true
    },
    roomType: {
        type: Array,
        required: true
    }
})
const formData = ref({
    name: '', // 房间名称，必选；
    building: '', // 所属楼层区域节点，必选；
    room_type: '', // 房间类型，可选，默认值：0；
    capacity: '', // 房间容量(容纳几人)，可选；
    area_size: '', // 面积大小，单位：平方米，可选；
    list_order: 1, // 排序值，可选，默认值：0；
    remark: '', // 备注，可选；
    status: 1, // 状态，可选，默认值：1；
    visible: 1 // 可见性，可选，默认值：1；
})
const rules = ref({
    building: [{ required: true, trigger: 'change', message: '请选择' }],
    name: [{ required: true, trigger: 'blur', message: '请输入房间名称' }],
    room_type: [{ required: true, trigger: 'change', message: '请选择房间类型' }],
    area_size: [
        {
            required: false,
            trigger: 'blur',
            validator: function (rule, value, callback) {
                if (value) {
                    const reg = /^[+-]?(\d+([.]\d*)?([eE][+-]?\d+)?|[.]\d+([eE][+-]?\d+)?)$/
                    if (!reg.test(value)) {
                        callback(new Error('房间面积必须为数字'))
                    } else {
                        callback()
                    }
                } else {
                    callback()
                }
            }
        }
    ]
})
const submitLoading = ref(false)
const unit = ref([])
const closed = () => {
    emits('update:modelValue', false)
    unit.value = []
    roomeForm.value.resetFields()
}
const handleChangeUnit = () => {
    nextTick(() => {
        roomeForm.value.clearValidate()
    })
    props.sourceData.forEach((ele) => {
        unit.value = ele.children || []
    })
}
const onConfirm = () => {
    roomeForm.value.validate(async (vali) => {
        if (vali) {
            try {
                submitLoading.value = true
                const params = { ...formData.value }
                if (!params.capacity) {
                    params.capacity = null
                }
                if (params.area_size) {
                    params.area_size = String(params.area_size)
                } else {
                    params.area_size = null
                }
                if (props.roomId) {
                    await putRooming(params)
                } else {
                    await addRooming(params)
                }
                emits('updateTable')
                submitLoading.value = false
                ElMessage.success(props.roomId ? '修改成功！' : '添加成功！')
                closed()
            } catch (error) {
                console.log(error)
                submitLoading.value = false
            }
        }
    })
}
const changeBuilding = (data) => {
    if (data.length) {
        formData.value.building = data[data.length - 1]
    } else {
        formData.value.building = ''
    }
}

// 项目、楼层区域回显数据
const getPath = async (path) => {
    console.log('🚀 ~ getPath ~ path:', path)
    const temp = ['', []]
    path.forEach((item, index) => {
        if (index === 0) {
            temp[index] = item
        } else {
            temp[1].push(item)
        }
    })
    await handleChangeUnit()
    if (temp && temp[1]) {
        formData.value.building = temp[1][temp[1].length - 1]
    } else {
        formData.value.building = ''
    }
}
watch(
    () => props.modelValue,
    async (newVal) => {
        if (newVal) {
            if (props.roomId) {
                getRoomDetail(props.roomId).then((data) => {
                    nextTick(async () => {
                        console.log('data', data)
                        formData.value.id = data.id
                        formData.value.name = data.name
                        formData.value.building = data.building
                        formData.value.area_size = data.area_size
                        formData.value.room_type = data.room_type.toString()
                        formData.value.status = data.status
                        formData.value.capacity = data.capacity
                        formData.value.list_order = data.list_order
                        formData.value.remark = data.remark
                        await getPath(findPatentValue(props.sourceData, data.building))
                    })
                })
            } else {
                await getPath(findPatentValue(props.sourceData, props.cascaderData[props.cascaderData.length - 1]))
            }
        }
    }
)
</script>

<style></style>
